/* eslint-disable prettier/prettier */
'use client';

import { useEffect, useMemo } from 'react';

import { isEmpty } from 'lodash';
import dynamic from 'next/dynamic';
import aa from 'search-insights';

import brandMobileLogo from '@/assets/images/logo-mobile.svg';
import brandLogo from '@/assets/images/logo.svg';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import BrandingBar from '@/components/V2/Header/BrandingBar';
import Menu from '@/components/V2/Header/Menu';
import SearchBar from '@/components/V2/Header/SearchBar';
import SearchBarProvider from '@/components/V2/Header/SearchBar/SearchBarProvider';
import TopCodeBar from '@/components/V2/Header/TopCodeBar';
import TopHeaderContainer from '@/components/V2/TopHeaderContainer';
import { ALGOLIA_ANALYTICS_KEY, ALGOLIA_APPLICATION_ID } from '@/config/env';
import {
  IbmPlexItalic,
  IbmPlexItalicBold,
  IbmPlexItalicMedium,
  IbmPlexItalicSemiBold,
  SweetSansPro,
  SweetSansProBold,
  SweetSansProExtraLight,
  SweetSansProLight,
  SweetSansProMedium,
  SweetSansProSemiBold,
} from '@/config/fonts';
import { useLogout } from '@/helpers/hooks/useLogout';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { generateApproperiateURL } from '@/helpers/utils/urlHelpers';
import { useAppSelector } from '@/store';
import { CustomLink } from '@/types/Common';

import { useAppContext } from '../AppProvider';
import Container from '../Container';

const PawPoints = dynamic(() => import('./Common/PawPoints'));
const TrackUserFullStory = dynamic(() => import('./FS/TrackUserFullStory'));
const ReplicatedSignIn = dynamic(() => import('./Replicated/SignIn'));
const EzShipOrSimpleCart = dynamic(() => import('./Common/EzShipOrSimpleCart'));
const BackofficeSampleCredits = dynamic(() => import('./Backoffice/SampleCredit'));
const ReplicatedUserDropDown = dynamic(() => import('./Replicated/BrandingBarDropdown'));
const BackofficeUserDropDown = dynamic(() => import('./Backoffice/BrandingBarDropdown'));

export default function Header() {
  const { webalias, generateURL } = useWebAlias();

  const { isBackOffice } = useAppContext();
  const { customer, isLoggedIn } = useAuthContext();

  useEffect(() => {
    aa('init', {
      appId: ALGOLIA_APPLICATION_ID,
      apiKey: ALGOLIA_ANALYTICS_KEY,
      useCookie: true,
    });
    window.aa = aa;
  }, []);

  const petProfileUrl = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias ?? ''}/petprofile`
    : generateURL('/petprofile', webalias);

  const { logout } = useLogout(isBackOffice, webalias);

  const userSettings = useMemo(() => {
    if (!isBackOffice) {
      return [
        { name: 'Account Settings', link: generateURL('/account/settings', webalias) },
        { name: 'Orders', link: generateURL('/orders', webalias) },
        { name: 'EZ Ships', link: generateURL('/auto-order/auto-order-list', webalias) },
        { name: 'Addresses', link: generateURL('/account/addresses', webalias) },
        { name: 'Payment Methods', link: generateURL('/account/paymentmethods', webalias) },
        { name: 'Fund A Gift Card', link: generateURL('/giftcards/fundgiftcard', webalias) },
        // { name: 'Manage Custom Food Labels', link: generateURL('/foodlabel/managelabels', webalias) },
        {
          name: 'Sign out',
          link: '#',
          onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();
            logout();
          },
        },
      ] as CustomLink[];
    } else {
      return [
        { name: 'View Profile', link: `/profile?token=${customer?.token ?? ''}` },
        { name: 'EZ Ships', link: '/AutoOrders/auto-order-list' },
        // { name: 'Manage Custom Food Labels', link: '/foodlabel/managelabels' },
        { name: 'Orders', link: '/orders' },
        { name: 'Account Settings', link: '/account/settings' },
        {
          name: 'Sign out',
          link: '#',
          onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();
            logout();
          },
        },
      ] as CustomLink[];
    }
  }, [customer?.token, generateURL, isBackOffice, logout, webalias]);

  let logoRedirectLink = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias ?? ''}`
    : generateApproperiateURL('/', webalias, isBackOffice);

  const isAuthenticationLoading = useAppSelector(store => store.slices.authenticationSlice.isAuthenticationLoading);

  return (
    <div
      className={cn(
        SweetSansProExtraLight.variable,
        SweetSansProLight.variable,
        SweetSansPro.variable,
        SweetSansProMedium.variable,
        SweetSansProSemiBold.variable,
        SweetSansProBold.variable,
        IbmPlexItalic.variable,
        IbmPlexItalicBold.variable,
        IbmPlexItalicMedium.variable,
        IbmPlexItalicSemiBold.variable,
        'v2-mb-5'
      )}
    >
      <TopCodeBar>
        <TopHeaderContainer isBackOffice={isBackOffice} />
      </TopCodeBar>
      <Container className="sm:v2-px-0 xl:v2-px-0 lg:v2-px-0 v2-py-[10px] sm:v2-py-[8px] xs:v2-py-[8px] md:v2-px-8 v2-px-4">
        <BrandingBar>
          <BrandingBar.CurrentUserSection
            href={'#'}
            className="sm:v2-min-w-[33.33%] v2-min-w-[100px] v2-text-sm lg:v2-text-base"
            userSettings={userSettings}
          />

          <BrandingBar.BrandingLink
            brandImage={brandLogo}
            href={logoRedirectLink}
            target={isBackOffice ? '_blank' : undefined}
            // className="v2-basis-auto v2-block"
            // logoClassName="md:!v2-w-[200px] v2-w-[100px]"
            className="v2-hidden v2-basis-auto lg:v2-block"
            prefetch={!isBackOffice}
          />
          <BrandingBar.BrandingLink
            brandImage={brandMobileLogo}
            href={logoRedirectLink}
            target={isBackOffice ? '_blank' : undefined}
            // className="v2-basis-auto v2-block"
            logoClassName="v2-w-[100px]"
            className="lg:v2-hidden v2-basis-auto v2-block"
            prefetch={!isBackOffice}
          />

          <BrandingBar.NavigationSection className="v2-min-w-[33.33%] v2-basis-auto v2-text-sm lg:v2-text-base v2-relative">
            <a
              href={petProfileUrl}
              target={isBackOffice ? '_blank' : undefined}
              className="v2-text-xs v2-font-normal v2-font-sweet-sans-pro v2-duration-300 v2-text-primary-700 lg:v2-hidden md:v2-hidden sm:v2-hidden xs:v2-hidden v2-block"
            >
              Pet Quiz
            </a>
            {!isBackOffice && isLoggedIn && !isEmpty(customer) && <ReplicatedUserDropDown />}

            {isBackOffice && isLoggedIn && !isEmpty(customer) && <BackofficeUserDropDown />}

            {!isEmpty(customer) && isLoggedIn && <PawPoints />}

            {isLoggedIn === false && !isBackOffice && !isAuthenticationLoading && <ReplicatedSignIn />}
            <EzShipOrSimpleCart />
          </BrandingBar.NavigationSection>
        </BrandingBar>
      </Container>
      <SearchBarProvider>
        <SearchBar />
        {/* <BrandingBar className="!v2-flex v2-items-center v2-justify-center lg:!v2-hidden">
          <BrandingBar.BrandingLink
            brandImage={brandLogo}
            href={logoRedirectLink}
            as={isBackOffice ? 'a' : 'Link'}
            target={isBackOffice ? '_blank' : undefined}
            className="v2-w-full v2-py-[16px]"
            logoClassName="!v2-w-[172px]"
          />
        </BrandingBar> */}

        <Menu />
      </SearchBarProvider>

      {isLoggedIn && <TrackUserFullStory />}
    </div>
  );
}
